import {
  ChangeEvent as ReactChangeEvent,
  FC,
  Fragment,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getEasyLayout,
  RemoteProcedureCall,
  useApplicationStateLoader,
  useApplicationStateValue,
} from '@resistdesign/voltra/app/utils';
import { APP_ENV_CONFIG } from '../config';
import { ADMIN } from '../../api/AppRoutes/AdminRoutes/IDs';
import { InterviewStatus, User, UserListInfo, UserWithStatusInfo } from '../../common/Types';
import { SYM } from '../Components/MaterialSymbol';
import styled from 'styled-components';
import { ProfileDetails } from './Admin/ProfileDetails';
import { AdminTools } from './Admin/AdminTools';
import { TabItem, Tabs } from '../Components/Tabs';
import { InterviewStatusLabel } from './Admin/InterviewStatusLabel';

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

enum AdminSearchMode {
  USER = 'USER',
  ID_CODE = 'ID_CODE',
  INTERVIEW_STATUS = 'INTERVIEW_STATUS',
}

type AdminSearchModeTabItem = TabItem & {
  mode: AdminSearchMode;
};

const SEARCH_MODE_LABELS: Record<AdminSearchMode, string> = {
  [AdminSearchMode.USER]: 'User',
  [AdminSearchMode.ID_CODE]: 'ID Code',
  [AdminSearchMode.INTERVIEW_STATUS]: 'Interview Status',
};

const SEARCH_MODE_RPC_PATHS: Record<AdminSearchMode, string> = {
  [AdminSearchMode.USER]: '/list-users',
  [AdminSearchMode.ID_CODE]: '/list-users-by-id-code',
  [AdminSearchMode.INTERVIEW_STATUS]: '/list-users-by-interview-status',
};

const INTERVIEW_STATUS_LABELS: Record<InterviewStatus, string> = {
  INTERVIEW_NOT_STARTED: 'Interview Not Started',
  INTERVIEW_STARTED: 'Interview Started',
  LAST_QUESTION_STARTED: 'Last Question Started',
  INTERVIEW_COMPLETED: 'Interview Completed',
  INTERVIEW_EXPIRED: 'Interview Expired',
};

const ProfileDropArrowTD = styled.td`
  cursor: pointer;
`;
const {
  layout: Layout,
  areas: { Header, Content },
} = getEasyLayout(styled.div`
  flex: 1 0 auto;
  height: 100%;
`)`
  header, auto
  content, 1fr
  \\ 1fr
`;
const ProfileListContent = styled(Content)`
  overflow: auto;
`;
const SearchModeBar = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5em;
`;
const SearchModeBarLabel = styled.div`
  flex: 0 0 auto;
`;
const SearchModeBarTabs = styled(Tabs).attrs({
  className: 'search-mode-tabs',
})`
  &.search-mode-tabs {
    width: unset;

    & > button {
      padding: 0.125em 1em;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`;

export type AdminProps = {
  authorization: string;
};

export const Admin: FC<AdminProps> = ({ authorization }) => {
  const [searchMode, setSearchMode] = useState<AdminSearchMode>(AdminSearchMode.USER);
  const searchModeTabItems = useMemo<AdminSearchModeTabItem[]>(
    () =>
      Object.values(AdminSearchMode).map(
        (mode) =>
          ({
            mode,
            label: SEARCH_MODE_LABELS[mode],
            onClick: () => setSearchMode(mode),
          }) as AdminSearchModeTabItem
      ),
    []
  );
  const selectedSearchModeIndex = useMemo<number>(
    () => searchModeTabItems.findIndex((item) => item.mode === searchMode),
    [searchMode, searchModeTabItems]
  );
  const [selectedInterviewStatus, setSelectedInterviewStatus] = useState<InterviewStatus>(
    InterviewStatus.INTERVIEW_COMPLETED
  );
  const onSelectedInterviewStatusChange = useCallback(
    ({ target: { value = InterviewStatus.INTERVIEW_COMPLETED } }: ReactChangeEvent<HTMLSelectElement>) => {
      const realValue: InterviewStatus = Object.values(InterviewStatus).includes(value as InterviewStatus)
        ? (value as InterviewStatus)
        : InterviewStatus.INTERVIEW_COMPLETED;

      setSelectedInterviewStatus(realValue);
    },
    []
  );
  const [openProfileMap, setOpenProfileMap] = useState<{ [key: string]: boolean }>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const USER_LIST_RPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/admin',
        authorization,
      },
      path: SEARCH_MODE_RPC_PATHS[searchMode],
    }),
    [authorization, searchMode]
  );
  const { value: userListInfoValue = {} } = useApplicationStateValue(ADMIN.LIST_USERS);
  const { cursor, users: userList = [] } = userListInfoValue as UserListInfo;
  const sortLatestUserList = useMemo(
    () =>
      userList.sort(
        ({ date_created: dateCreatedA }: User, { date_created: dateCreatedB }: User) => dateCreatedB - dateCreatedA
      ),
    [userList]
  );
  const { loading: userListLoading = false, makeRemoteProcedureCall: loadUserList } = useApplicationStateLoader({
    identifier: ADMIN.LIST_USERS,
    remoteProcedureCall: USER_LIST_RPC,
    manual: true,
  });
  const onSearchInputChange = useCallback(
    ({ target: { value } }: ReactChangeEvent<HTMLInputElement>) => {
      setSearchTerm(value);
    },
    [setSearchTerm]
  );
  const onToggleProfileOpen = useCallback(({ currentTarget: { dataset } }: ReactMouseEvent<HTMLTableCellElement>) => {
    const { profileId } = dataset as Record<string, string>;

    setOpenProfileMap((prevOpenProfileMap) => ({
      ...prevOpenProfileMap,
      [profileId]: !prevOpenProfileMap[profileId],
    }));
  }, []);
  const modeDependentSearchTerm = useMemo<string>(
    () => (searchMode === AdminSearchMode.INTERVIEW_STATUS ? (selectedInterviewStatus as string) : searchTerm),
    [searchMode, selectedInterviewStatus, searchTerm]
  );
  const onLoadMoreUsers = useCallback(() => {
    loadUserList(modeDependentSearchTerm, cursor);
  }, [loadUserList, modeDependentSearchTerm, cursor]);
  const showLoadMoreButton = cursor || (!searchTerm && userList.length < 1);

  useEffect(() => {
    const tiD = setTimeout(() => {
      if (modeDependentSearchTerm.length > 0) {
        loadUserList(modeDependentSearchTerm);
      }
    }, 800);

    return () => clearTimeout(tiD);
  }, [modeDependentSearchTerm, loadUserList]);

  return (
    <Layout data-id="ADMIN">
      <Header>
        <SearchModeBar>
          <SearchModeBarLabel>Search By:</SearchModeBarLabel>
          <SearchModeBarTabs selectedIndex={selectedSearchModeIndex} tabItems={searchModeTabItems} />
        </SearchModeBar>
        {searchMode === AdminSearchMode.INTERVIEW_STATUS ? (
          <select value={selectedInterviewStatus} onChange={onSelectedInterviewStatusChange}>
            {Object.keys(INTERVIEW_STATUS_LABELS).map((status) => (
              <option key={status} value={status}>
                {INTERVIEW_STATUS_LABELS[status as InterviewStatus]}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={searchTerm}
            onChange={onSearchInputChange}
            placeholder="Search for users..."
            autoFocus
          />
        )}
      </Header>
      <ProfileListContent data-id="CONTENT">
        {userListLoading ? (
          <article aria-busy={userListLoading ? true : undefined}></article>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Show Profile</th>
                <th>Username</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                {searchMode === AdminSearchMode.INTERVIEW_STATUS ? <th>Status</th> : undefined}
                {searchMode === AdminSearchMode.INTERVIEW_STATUS ? <th>Status Timestamp</th> : undefined}
              </tr>
            </thead>
            <tbody>
              {sortLatestUserList.map((userItem, index) => {
                const { username, given_name, family_name, email, phone_number, sub, status, statusTimestamp } =
                  userItem as UserWithStatusInfo;

                return (
                  <Fragment key={`User:${index}`}>
                    <tr>
                      <ProfileDropArrowTD data-profile-id={sub} onClick={onToggleProfileOpen}>
                        <SYM>{openProfileMap[sub] ? 'arrow_drop_down' : 'arrow_right'}</SYM>
                      </ProfileDropArrowTD>
                      <td>{username}</td>
                      <td>
                        {given_name} {family_name}
                      </td>
                      <td>
                        <a href={`mailto:${email}`}>{email}</a>
                      </td>
                      <td>
                        <a href={`tel:${phone_number}`}>{phone_number}</a>
                      </td>
                      {searchMode === AdminSearchMode.INTERVIEW_STATUS ? (
                        <td>
                          <InterviewStatusLabel status={status} />
                        </td>
                      ) : undefined}
                      {searchMode === AdminSearchMode.INTERVIEW_STATUS ? (
                        <td>{typeof statusTimestamp === 'number' ? new Date(statusTimestamp).toLocaleString() : ''}</td>
                      ) : undefined}
                    </tr>
                    {openProfileMap[sub] ? (
                      <tr>
                        <td></td>
                        <td colSpan={searchMode === AdminSearchMode.INTERVIEW_STATUS ? 5 : 3}>
                          <ProfileDetails authorization={authorization} user={userItem} profileId={sub} />
                        </td>
                      </tr>
                    ) : undefined}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {showLoadMoreButton ? (
          <>
            <br />
            <button onClick={onLoadMoreUsers}>{cursor ? 'More...' : 'List All'}</button>
          </>
        ) : undefined}
        <hr />
        <AdminTools />
      </ProfileListContent>
    </Layout>
  );
};
